import { Injectable, inject } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { QuoteStore, QuoteToolLiteStore } from '../store';
import { RouteIds } from '../types';
import { GoogleAnalyticsParametersType } from './google-analytics-parameters.type';
import { QuoteDataLayerService } from './quote-data-layer.service';
import { QuoteDataLayerStrategy, QuoteFieldsBuilders } from './quote-data-layer.strategy';
import { Flows, Flow } from '@lx/quote-tool/lite/shared/types';

@Injectable({
  providedIn: 'root',
})
export class AdultQuoteDataLayerStrategy implements QuoteDataLayerStrategy {
  readonly flow: Flow = Flows.Adult;

  quoteFieldsBuilders: QuoteFieldsBuilders | undefined;

  private readonly quoteDataLayerService = inject(QuoteDataLayerService);
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly quoteStore = inject(QuoteStore);

  initializeDataLayerStepsBuilders(): void {
    this.quoteFieldsBuilders = {
      [RouteIds.course]: this.getQuoteBaseDataBuilder(),
      [RouteIds.extras]: this.getQuoteBaseDataBuilder(),
      [RouteIds.applicant]: this.getQuoteBaseDataBuilder(),
      [RouteIds.quote]: this.getAllQuoteRelatedDataBuilder(),
      [RouteIds.booking]: this.getAllQuoteRelatedDataBuilder(),
      [RouteIds.confirmation]: this.getAllQuoteRelatedDataBuilder(),
      [RouteIds.payment]: this.getAllQuoteRelatedDataBuilder(),
    };
  }

  buildDataLayer(): Observable<Partial<GoogleAnalyticsParametersType>> {
    return this.quoteDataLayerService.buildQuoteDataLayerObject(this.quoteFieldsBuilders);
  }

  /**
   * Returns a function with the selectors that retrieves base data fields such as language, school, accommodation type, program of interest...
   * and build the data layer for the initial steps in the quote process.
   */
  private getQuoteBaseDataBuilder() {
    return () => this.getQuoteBaseData();
  }

  /**
   * Returns a function with the selectors that retrieves quote-related fields such as accommodation,
   * insurance, transfers, and prices. This function is used to build the data layer for specific steps in the quote process.
   * And build the data layer for the steps where all quote fields are available.
   */
  private getAllQuoteRelatedDataBuilder() {
    return () =>
      combineLatest([
        this.getQuoteBaseData(),
        this.quoteDataLayerService.getInsuranceData(),
        this.quoteDataLayerService.getTransfersData(),
        this.quoteDataLayerService.getProfessionalImmersionExperienceData(),
        this.getAccommodationData(),
        this.quoteDataLayerService.getPricesData(),
      ]).pipe(
        map(([baseData, insuranceData, transfersData, professionalImmersionExperienceData, accommodationData, prices]) => ({
          ...baseData,
          ...accommodationData,
          ...transfersData,
          ...insuranceData,
          ...professionalImmersionExperienceData,
          ...prices,
        })),
      );
  }

  private getQuoteBaseData() {
    return combineLatest([this.quoteDataLayerService.getBaseData(), this.quoteToolLiteStore.selectionForAdultFlowDataLayer$]).pipe(
      map(([baseData, { selectedLanguage, selectedSchoolLocation, selectedAccommodation }]) => ({
        ...baseData,
        quote_language_interest: selectedLanguage,
        quote_school_interest: selectedSchoolLocation?.schoolName,
        quote_accommodation_type: selectedAccommodation?.type,
      })),
    );
  }

  private getAccommodationData() {
    return this.quoteStore.accommodation$.pipe(
      map((accommodation) => ({
        quote_accommodation_name: accommodation?.name,
      })),
    );
  }
}
